<script setup lang="ts">
import {computed, inject, ref} from 'vue'
import {ElasticInput, TextInput} from '@hantera/design-system'
import {useLocalization} from '@hantera/portal-app'
import type {useClientStore} from '@/stores/iam/client-store'

const t = useLocalization('iam.clients')
const store = inject<ReturnType<typeof useClientStore>>('clientStore')!

const editing = ref(false)
const editForm = ref({
  name: '',
  description: '',
})

const canWrite = computed(() => store.canWrite)
const client = computed(() => store.client)

function startEdit() {
  if (!client.value) return

  editForm.value = {
    name: client.value.name || '',
    description: client.value.description || '',
  }
  editing.value = true
}

async function saveEdit() {
  if (!client.value) return

  try {
    await store.updateProfile({
      name: editForm.value.name || undefined,
      description: editForm.value.description || undefined,
    })
    editing.value = false
  } catch (error) {
    // Error handled by store
  }
}

function cancelEdit() {
  editing.value = false
}

function formatDateTime(date: string | undefined): string {
  if (!date) return '—'
  return new Date(date).toLocaleString()
}

function formatRelativeTime(date: string | undefined): string {
  if (!date) return t('status.never')

  const now = new Date()
  const then = new Date(date)
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) return t('time.justNow')
  if (seconds < 3600) return t('time.minutesAgo', { count: Math.floor(seconds / 60) })
  if (seconds < 86400) return t('time.hoursAgo', { count: Math.floor(seconds / 3600) })
  return t('time.daysAgo', { count: Math.floor(seconds / 86400) })
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text)
  store.processes.createProcess({
    description: t('actions.copied'),
  }).complete()
}

async function handleDelete() {
  await store.deleteClient()
}

</script>

<template>
  <div v-if="client" class="client-sidebar">
    <dl class="box-content">

      <dt>{{ t('fields.name') }}</dt>
      <dd>
        <TextInput v-if="editing" v-model="editForm.name" :placeholder="t('placeholders.name')" />
        <span v-else>{{ client.name || '—' }}</span>
      </dd>

      <dt>{{ t('fields.description') }}</dt>
      <dd>
        <ElasticInput
          v-if="editing" 
          v-model="editForm.description" 
          :placeholder="t('placeholders.description')"
          rows="3"
          class="form-control"
        />
        <span v-else>{{ client.description || '—' }}</span>
      </dd>
    </dl>

    <dl class="box-content" v-if="!editing">
      <dt>{{ t('fields.clientId') }}</dt>
      <dd class="monospace">
        {{ client.id }}
      </dd>
      
      <dt>{{ t('fields.created') }}</dt>
      <dd>{{ formatDateTime(client.createdAt) }}</dd>

      <dt>{{ t('fields.lastActive') }}</dt>
      <dd>{{ formatRelativeTime(client.lastActiveAt) }}</dd>
    </dl>

    <div class="sidebar-actions">
      <button v-if="!editing && canWrite" @click="startEdit">
        {{ t('actions.edit') }}
      </button>
      <button v-if="editing" @click="saveEdit" class="btn-primary">
        {{ t('actions.save') }}
      </button>
      <button v-if="editing" @click="cancelEdit">
        {{ t('actions.cancel') }}
      </button>

      <button v-if="canWrite && !editing" @click="handleDelete" class="btn-danger">
        {{ t('actions.delete') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.client-sidebar {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem;
}

.monospace {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

.sidebar-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
}

.form-control {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.875rem;
  resize: vertical;
}

.form-control:focus {
  outline: none;
  border-color: var(--color-primary);
}

.btn-icon {
  padding: 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
}

.btn-icon:hover {
  opacity: 0.7;
}
</style>
